/** Account → Alerts: price and portfolio alerts, push on this device, and what fired recently. */
import { useEffect, useState } from 'react';
import { ALERT_KIND_LABEL, ALERTS_MAX_ACTIVE, ECON_CATEGORY_LABEL, pairLabel, type Alert, type EconCategory } from '@platform/shared';
import { cx, Icon, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import {
  clearAlertHistory,
  deleteAlert,
  disablePush,
  enablePush,
  loadAlertEvents,
  loadAlerts,
  openAlertBuilder,
  openKeyEvents,
  pushSubscribed,
  pushSupported,
  updateAlert,
  useAlertEvents,
  useAlerts,
} from '../../stores/alerts';
import { loadWatchlists, useWatchlists } from '../../stores/markets';
import { Card, Chip, errMsg, H1, H2, Notice, PillButton, when } from './kit';

/** One-line summary of what an alert watches. */
export function alertSummary(a: Alert, watchlistName?: string): string {
  const p = a.params as Record<string, any>;
  const s = a.symbol ? pairLabel(a.symbol) : '';
  switch (a.kind) {
    case 'price_above':
      return `${s} rises above ${p.price}`;
    case 'price_below':
      return `${s} falls below ${p.price}`;
    case 'move':
      return `${s} ${p.direction === 'up' ? 'rises' : p.direction === 'down' ? 'falls' : 'moves'} ${p.pct}% in ${p.window}`;
    case 'range_break':
      return `${s} breaks its ${p.window} ${p.side}`;
    case 'market_open':
      return `${s} market opens`;
    case 'event':
      return `${p.minutesBefore} min before ${p.every ? 'every ' : ''}${p.title}${p.result ? ', with the result' : ''}`;
    case 'key_events': {
      const cats = (p.categories ?? []).map((c: string) => ECON_CATEGORY_LABEL[c as EconCategory]?.toLowerCase()).filter(Boolean);
      const what = [p.high ? 'high-impact releases' : null, cats.length ? `${cats.join(', ')} releases` : null].filter(Boolean).join(' and ');
      return `${p.minutesBefore} min before ${what}${p.currencies?.length ? ` (${p.currencies.join(', ')})` : ''}${p.result ? ', with results' : ''}`;
    }
    case 'watchlist_move':
      return `Anything in ${watchlistName ?? 'a watchlist'} moves ${p.pct}% in ${p.window}`;
    case 'position_pnl':
      return `${s ? `${s} position` : 'A position'} is ${p.direction === 'loss' ? 'down' : 'up'} $${p.usd}`;
    case 'tp_sl_near':
      return `Price within ${p.pct}% of a TP or SL`;
    case 'equity_drawdown':
      return `Equity down ${p.pct}% from today's high`;
    case 'margin_level':
      return `Margin level at or below ${p.pct}%`;
    case 'concentration':
      return `One instrument over ${p.pct}% of open positions`;
    case 'daily_summary':
      return `Daily summary at ${String(p.hourUtc).padStart(2, '0')}:00 UTC`;
  }
}

function PushCard() {
  const [state, setState] = useState<{ on: boolean; devices: number; enabled: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => {
    const [on, d] = await Promise.all([pushSubscribed().catch(() => false), api.get<{ enabled: boolean; devices: number }>('/api/push/devices').catch(() => ({ enabled: false, devices: 0 }))]);
    setState({ on, devices: d.devices, enabled: d.enabled });
  };
  useEffect(() => void refresh(), []);
  const run = async (fn: () => Promise<string | null | void>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const err = await fn();
      setMsg(err ? { ok: false, text: err } : { ok: true, text: ok });
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
      void refresh();
    }
  };
  if (!state) return null;
  return (
    <div className="rounded-2xl border border-line p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[16px] font-semibold">Push notifications on this device</div>
          <div className="mt-1 text-[14px] text-muted">
            {!state.enabled
              ? 'Push isn’t set up on this platform yet. Alerts still arrive under the bell and by email.'
              : !pushSupported()
                ? 'This browser can’t receive push. On iPhone, add the app to your Home Screen and open it from there.'
                : state.on
                  ? `On. ${state.devices} device${state.devices === 1 ? '' : 's'} receive your alerts.`
                  : `Off on this device.${state.devices ? ` ${state.devices} other device${state.devices === 1 ? '' : 's'} receive your alerts.` : ''}`}
          </div>
        </div>
        {state.enabled && pushSupported() && (
          <div className="flex gap-2">
            {state.on && (
              <PillButton size="sm" kind="secondary" busy={busy} onClick={() => run(async () => void (await api.post('/api/push/test')), 'Test sent. It should appear in a few seconds.')}>
                Send a test
              </PillButton>
            )}
            <PillButton size="sm" kind={state.on ? 'secondary' : 'accent'} busy={busy} onClick={() => run(state.on ? disablePush : enablePush, state.on ? 'Push is off on this device.' : 'Push is on for this device.')}>
              {state.on ? 'Turn off' : 'Turn on'}
            </PillButton>
          </div>
        )}
      </div>
      {msg && <Notice msg={msg} onClose={() => setMsg(null)} />}
    </div>
  );
}

const CHANNEL: Record<string, string> = { bell: 'App', push: 'Push', email: 'Email' };

/** Key economic events: one setting, kept with the other alerts; plus the position warning everyone gets. */
function KeyEventsCard({ alert }: { alert: Alert | null }) {
  return (
    <div className="rounded-2xl border border-line p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[16px] font-semibold">Key economic events</div>
          <div className="mt-1 text-[14px] text-muted">
            {alert ? (alert.active ? `On: ${alertSummary(alert)}.` : 'Paused.') : 'Get a reminder before important releases, such as US crude oil inventories or a rate decision, and the result when it is out.'}
          </div>
          <div className="mt-1 text-[13px] text-muted">
            High-impact releases for markets you hold come as a warning 30 minutes before, unless you turn off “High-impact events for markets held” in{' '}
            <Link to="/account/notifications/settings" className="text-accent-fg">
              notifications
            </Link>
            .{' '}
            <Link to="/markets/calendar" className="text-accent-fg">
              Open the calendar
            </Link>
          </div>
        </div>
        <PillButton size="sm" kind={alert ? 'secondary' : 'accent'} onClick={() => void openKeyEvents()}>
          {alert ? 'Edit' : 'Set up'}
        </PillButton>
      </div>
    </div>
  );
}

export function AlertsPage() {
  const alerts = useAlerts();
  const events = useAlertEvents();
  const lists = useWatchlists();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    void loadAlerts();
    void loadAlertEvents();
    void loadWatchlists();
  }, []);
  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(null);
    }
  };
  const active = (alerts ?? []).filter((a) => a.active).length;

  return (
    <Card>
      <H1>Alerts</H1>
      <p className="m-0 mt-2 text-[16px] text-muted md:text-[17px]">
        Get told when a price moves, a market opens, an event is coming or your positions need attention. Set one from any instrument on{' '}
        <Link to="/markets" className="text-accent-fg">
          Markets
        </Link>{' '}
        or here.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        <PillButton size="sm" kind="accent" onClick={() => openAlertBuilder({ symbol: 'BTC' })}>
          New price alert
        </PillButton>
        <PillButton size="sm" kind="secondary" onClick={() => openAlertBuilder({ kind: 'position_pnl' })}>
          New portfolio alert
        </PillButton>
        <PillButton size="sm" kind="secondary" onClick={() => openAlertBuilder({ kind: 'daily_summary', params: { hourUtc: 21 } })}>
          Daily summary
        </PillButton>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <PushCard />
        <KeyEventsCard alert={(alerts ?? []).find((a) => a.kind === 'key_events') ?? null} />
        <Notice msg={msg} onClose={() => setMsg(null)} />

        <div className="flex items-baseline justify-between gap-3">
          <H2>Your alerts</H2>
          <span className="text-[13px] text-muted">
            {active} of {ALERTS_MAX_ACTIVE} active
          </span>
        </div>
        {!alerts ? (
          <div className="grid place-items-center py-8 text-muted">
            <Spinner />
          </div>
        ) : alerts.length ? (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {alerts.map((a) => (
              <li key={a.id} className={cx('flex flex-wrap items-center gap-3 rounded-2xl border border-line px-4 py-3', !a.active && 'opacity-70')}>
                <span className={cx('grid size-9 shrink-0 place-items-center rounded-full', a.active ? 'bg-track text-fg' : 'bg-track text-muted')}>
                  <Icon name="alert" size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold">{alertSummary(a, lists?.find((l) => l.id === (a.params as any).watchlistId)?.name)}</div>
                  <div className="mt-0.5 text-[12.5px] text-muted">
                    {ALERT_KIND_LABEL[a.kind]} · {a.channels.map((c) => CHANNEL[c]).join(', ')} · {a.repeat === 'once' ? 'Once' : a.repeat === 'daily' ? 'Daily' : 'Every time'}
                    {a.fireCount > 0 && ` · fired ${a.fireCount}×, last ${when(a.lastFiredAt!)}`}
                    {a.expiresAt && a.active && ` · until ${new Date(a.expiresAt).toLocaleDateString()}`}
                    {a.note && ` · “${a.note}”`}
                  </div>
                </div>
                <Chip tone={a.active ? 'up' : 'muted'}>{a.active ? 'Active' : a.fireCount && a.repeat === 'once' ? 'Fired' : 'Paused'}</Chip>
                <div className="flex gap-1">
                  <button type="button" disabled={busy === a.id} onClick={() => act(a.id, () => updateAlert(a.id, { active: !a.active }))} className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-accent-fg hover:bg-raised disabled:opacity-50">
                    {a.active ? 'Pause' : 'Resume'}
                  </button>
                  <button type="button" onClick={() => openAlertBuilder({ alert: a })} className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold hover:bg-raised">
                    Edit
                  </button>
                  <button type="button" disabled={busy === a.id} onClick={() => act(a.id, () => deleteAlert(a.id))} aria-label="Delete alert" className="rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-down hover:bg-raised disabled:opacity-50">
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 rounded-2xl border border-dashed border-line px-4 py-8 text-center text-muted">No alerts yet. Tap the bell next to any instrument on Markets to add one.</p>
        )}

        <div className="mt-4 flex items-baseline justify-between gap-3">
          <H2>Recently fired</H2>
          {!!events?.length && (
            <button type="button" onClick={() => act('history', clearAlertHistory)} className="text-[13px] font-semibold text-muted hover:text-fg">
              Clear history
            </button>
          )}
        </div>
        {events?.length ? (
          <ul className="m-0 flex list-none flex-col p-0">
            {events.map((e) => (
              <li key={e.id} className="flex items-start justify-between gap-3 border-b border-line py-3 last:border-b-0">
                <div className="min-w-0">
                  <div className="text-[14.5px] font-semibold">{e.title}</div>
                  <div className="mt-0.5 text-[13px] text-muted">{e.body}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="text-[12px] text-muted">{when(e.firedAt)}</span>
                  {e.link && (
                    <Link to={e.link} className="text-[12.5px] font-semibold text-accent-fg no-underline">
                      Open
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-[14px] text-muted">Nothing has fired yet.</p>
        )}
      </div>
    </Card>
  );
}
